<template>
  <label
    class="radio"
    :class="classes"
  >
    <div class="radio__content">
      <input
        :id="$props.inputId"
        ref="radioRef"
        v-bind="$attrs"
        class="radio__input outline"
        type="radio"
        tabindex="0"
        :checked="checked"
        :disabled="$props.disabled"
        :name="$props.name"
        @change="toggle"
        @blur="handleBlur"
      >

      <div class="radio__icon">
        <div class="" />
        <the-icon
          class="drop"
          :art="['duotone', 'regular']"
          name="circle"
        />
        <the-icon
          :art="['duotone', 'regular']"
          name="circle"
          @keydown="onKeyDown"
        />
      </div>

      <slot name="label">
        <label
          :for="$props.inputId"
          class="radio__label"
          :class="$props.size === 'sm' ? 'body-2' : 'body-1'"
          v-html="$props.label"
        />
      </slot>
    </div>
  </label>
</template>

<script setup>
  import { computed, getCurrentInstance, ref, useId } from 'vue'

  // INIT
  const model = defineModel({ type: [String, Number, Boolean, undefined] })
  const props = defineProps({
    disabled: {
      type: Boolean,
      default: false,
    },

    inputId: {
      type: String,
      default: () => useId()
    },

    isInvalid: {
      type: Boolean,
      default: false,
    },

    label: {
      type: String,
      default: null,
    },

    name: {
      type: String,
      required: true,
    },

    size: {
      validator(value) {
        return ['sm', 'md'].includes(value)
      },
      default: 'sm',
    },
  })

  // DATA
  const radioRef = ref()

  // COMPUTED
  const attrValue = computed(() => {
    return getCurrentInstance().attrs.value
  })

  const checked = computed(() => {
    return props.modelValue === attrValue.value
  })

  const classes = computed(() => {
    return {
      [`radio--${props.size}`]: true,
      'radio--selected': checked.value,
      'radio--disabled': props.disabled,
      'radio--error': props.isInvalid,
    }
  })

  // METHODS
  function handleBlur() {
    radioRef.value?.classList.remove('has-outline')
  }

  function onKeyDown(event) {
    if (event.code === 'Space') {
      toggle()
      event.preventDefault()
    }
  }

  function toggle() {
    if (!checked.value) {
      model.value = attrValue.value
    }
  }
</script>

<style name="mobile" scoped>
.radio {
  --radio-icon-c-color: var(--on-surface);
  --radio-icon-c-background: transparent;
  --radio-icon-c-label: var(--on-surface);
  --radio-icon-c-shadow-color: transparent;

  cursor: pointer;
  display: inline-block;
  position: relative;

  &--selected {
    --radio-icon-c-color: var(--Interaction-States-enabled-default);
    --radio-icon-c-background: var(--Interaction-States-enabled-default);
  }

  &--disabled {
    --radio-icon-c-color: var(--Interaction-States-disabled-default);
    --radio-icon-c-background: var(--Interaction-States-disabled-default);
    --radio-icon-c-label: var(--Interaction-States-disabled-default-label);
    pointer-events: none;
  }

  &.radio-disabled.radio--selected {
    --radio-icon-c-color: var(--Interaction-States-disabled-default);
    --radio-icon-c-background: var(--Interaction-States-disabled-default);
  }

  &--error {
    --radio-icon-c-color: var(--error);
  }

  &.radio--error.radio--selected {
    --radio-icon-c-color: var(--error);
    --radio-icon-c-background: var(--error);
  }

  &:hover {
    --radio-icon-c-color: var(--Interaction-States-hovered-default-a);
    --radio-icon-c-background: var(--Interaction-States-hovered-default-a);
    --radio-icon-c-shadow-color: var(--Interaction-States-hovered-default-b);
  }

  &:active {
    --radio-icon-c-color: var(--Interaction-States-pressed-default-a);
    --radio-icon-c-background: var(--Interaction-States-pressed-default-a);
    --radio-icon-c-shadow-color: var(--Interaction-States-pressed-default-c);
  }

  &--sm {
    --radio-icon-scale: 20px;
  }

  &--md {
    --radio-icon-scale: 24px;
  }
}

.radio__content {
  display: flex;
  flex-direction: row;
  column-gap: var(--fixed-spacing-fix-03);
  align-items: center;

  &:has(.radio__input.has-outline:focus-visible) .radio__icon .drop {
    --fa-primary-color: var(--on-surface);
  }
}

.radio__input {
  pointer-events: none;
  position: absolute;
  opacity: 0;
}

.radio__label {
  color: var(--radio-icon-c-label);
}

.radio__icon {
  font-size: var(--radio-icon-scale);
  position: relative;

  --fa-primary-color: var(--radio-icon-c-color);
  --fa-secondary-color: var(--radio-icon-c-background);
  --fa-secondary-opacity: 1;

  .drop {
    position: absolute;
    font-size: var(--radio-icon-scale);
    top: 3px;
    transform: scale(1.5);
    z-index: -1;

    --fa-primary-color: var(--radio-icon-c-shadow-color);
    --fa-secondary-color: var(--radio-icon-c-shadow-color);
  }

  .icon {
    transition: all 0.15s ease-in;
  }
}
</style>
